import React, { FunctionComponent, useState } from "react"
import { useMutation, useQuery } from "@apollo/client"
import { SelectionControlGroup, Icon } from "@kuali/pure-ui"
import { FormProvider, useForm, Controller } from "react-hook-form"
import { Alert, Spinner } from "@kuali/pure-ui"

import PillGroupWithComment from "../../pill/PillGroupWithComment"
import { LevelOfCriticality } from "./types/loc"
import RTOSelect from "./recovery-time-selection"
import { toggleLegacySaveButton } from "../../../utils/legacy-form-utils"
import { useParams } from "react-router-dom"

import { UPDATE_CRITICAL_FUCTION, GET_CRITICAL_FUNCTION } from "./queries"

import "./../../../base/forms.scss"
import "./styles.scss"

interface Props {
  formId: string
  criticalFunctionId: number
  isReactRightSidebarEnabled: boolean
  onFormChange: any
}

const CriticalityLevels: FunctionComponent<Props> = ({
  formId,
  criticalFunctionId,
  isReactRightSidebarEnabled,
  onFormChange
}) => {
  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [showFormSaveAlert, setShowFormSaveAlert] = useState(false)

  const [mostRecentFocusedEl, setIsMostRecentFocusedEl] = useState<
    React.RefObject<HTMLTextAreaElement>
  >(null)

  // manage focus when alert is closed and return to most recently focused el
  React.useEffect(() => {
    if (!isAlertOpen) {
      if (mostRecentFocusedEl && mostRecentFocusedEl.current) {
        mostRecentFocusedEl.current.focus()
      }
    }
  }, [isAlertOpen])

  // We can use the `useParams` hook here to access
  // the dynamic pieces of the URL.
  const { viewType } = useParams()
  // when page is not editable all interactive and form elements are set to disabled
  const isPageInEditMode = viewType === "edit"

  // instantiate `react-hook-form` and destructure methods for use
  const methods = useForm({
    mode: "onChange"
  })
  const {
    handleSubmit,
    formState,
    trigger,
    control,
    getValues,
    register,
    watch,
    errors
  } = methods

  const { isSubmitted } = formState

  // query to get critical function data to populate page
  const { data, loading, error: getLOCError } = useQuery(GET_CRITICAL_FUNCTION, {
    variables: {
      id: criticalFunctionId
    },
    errorPolicy: "all",
    onCompleted() {
      // console.log("completed get")
    },
    onError(error) {
      // console.log("error", error)
    }
  })

  // mutation to update critical function on form/event change
  const [
    updateCriticalFunction,
    { loading: isCriticalFunctionUpdating, error: updateError }
  ] = useMutation<{
    updateCriticalFunction: LevelOfCriticality
  }>(UPDATE_CRITICAL_FUCTION, {
    errorPolicy: "all",
    onCompleted({ updateCriticalFunction }) {
      // indicate form has been saved
      if (updateCriticalFunction) {
        if (!isReactRightSidebarEnabled) {
          toggleLegacySaveButton("saved")
        } else {
          onFormChange({
            needsSave: false,
            loading: false
          })
        }

        if (showFormSaveAlert) {
          // trigger form save success message - using legacy non-react view so
          // we fire event that will be listened to outside of React app. This
          // will be moved into full React app as we transition
          const customEvent = new CustomEvent("noticeChange", {
            bubbles: true,
            detail: {
              isOpen: true,
              message: "Critical Function has been updated",
              iconName: "check-circle",
              color: "green"
            }
          })
          window.dispatchEvent(customEvent)
        }

        // clear dirty state changeGuard uses to detect changes and show alert
        window.changeGuard.clearDirty()
      }
    },
    onError(error) {
      if (!isReactRightSidebarEnabled) {
        toggleLegacySaveButton("save")
      } else {
        // persist `save` as form was not `saved`
        onFormChange({
          needsSave: false,
          loading: false
        })
      }

      if (showFormSaveAlert) {
        // trigger form error message - using legacy non-react view so
        // we fire event that will be listened to outside of React app. This
        // will be moved into full React app as we transition
        const customEvent = new CustomEvent("noticeChange", {
          bubbles: true,
          detail: {
            isOpen: true,
            message: "Critical Function is not able to be updated",
            iconName: "alert-warning",
            color: "red"
          }
        })
        window.dispatchEvent(customEvent)
      }
      // clear dirty state changeGuard uses to detect changes and show alert
      window.changeGuard.clearDirty()
    }
  })

  const buildSchemaAndUpdateData = (formData) => {
    // make mutation request to update level of criticality based on form data

    updateCriticalFunction({
      variables: {
        id: criticalFunctionId,
        recoveryTimeObjectiveId: Number(formData.recoveryTimeObjectiveId),
        levelOfCriticality:
          formData.agreeWithSuggestedCriticality === "yes"
            ? data.criticalFunction.suggestedLevelOfCriticality
            : Number(formData.userSelectedLevelOfCriticality),
        agreeWithSuggestedCriticality:
          formData.agreeWithSuggestedCriticality === "yes" ? true : false,
        levelOfCriticalityExplanation: formData.levelOfCriticalityExplanation
          ? formData.levelOfCriticalityExplanation
          : null,
        criticalityQuestionAnswers: {
          ...formData.criticalityQuestionAnswers
        }
      }
    })
  }

  const handleValidFormSubmit = async (data) => {
    if (!isReactRightSidebarEnabled) {
      toggleLegacySaveButton("progress")
    } else {
      onFormChange({
        loading: true,
        needsSave: false
      })
    }

    // Note: The submit button for the form exists in legacy sidebar code and actual
    // form submit() is executed there, this callback is tied into react-hook-form detecting `submit`
    setIsAlertOpen(false)
    setShowFormSaveAlert(true)
    buildSchemaAndUpdateData(data)
  }

  const handleInvalidFormSubmit = (data) => {
    setIsAlertOpen(true)
  }

  const handleFormAnswerChange = async (event?) => {
    // regardless of form validity we never persist open Alert when just answers change
    setIsAlertOpen(false)
    // we don't show full alert if form has been updated by answering
    setShowFormSaveAlert(false)

    const isFormValid = await trigger()

    if (isFormValid) {
      const formData = getValues()

      // when form is valid we update the critical function with new answers,
      // refreshing our data with any new suggestions as a result
      updateCriticalFunction({
        variables: {
          id: criticalFunctionId,
          recoveryTimeObjectiveId: Number(formData.recoveryTimeObjectiveId),
          criticalityQuestionAnswers: {
            ...formData.criticalityQuestionAnswers
          }
        }
      })
    }
  }

  const handleFormUpdate: any = () => {
    if (isReactRightSidebarEnabled) {
      onFormChange({
        needsSave: true,
        loading: false
      })
    }
  }

  const handlePillChange: any = () => {
    // only handle form answer request after subsequent changes to pills,
    // cuts down on erroneous calls and validation checks
    if (data.criticalFunction.suggestedLevelOfCriticality || isSubmitted) {
      handleFormAnswerChange()
    }
  }

  const handleTextareaFocus: any = (
    focusedEl: React.RefObject<HTMLTextAreaElement>
  ) => {
    if (focusedEl && focusedEl.current) {
      setIsMostRecentFocusedEl(focusedEl)
    }
  }

  if (getLOCError || updateError) {
    return (
      <div className="stack box">
        <span className="validation-error">
          <Icon name={"alert-warning"} /> Unable to complete request at this time
        </span>
      </div>
    )
  }

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        {/* Wrapping form tag needed at top level for submit and change tracking
        In addition data attribute added to track if alert should show for unsaved changes -
        see change-guard-coffee.js */}
        <form
          id={formId}
          onSubmit={handleSubmit(handleValidFormSubmit, handleInvalidFormSubmit)}
          onChange={handleFormUpdate}
          data-track-confirm-unsaved-changes="post"
        >
          {loading && (
            <div className="stack box">
              <div className="center">
                <Spinner size={64} />
              </div>
            </div>
          )}
          {data && !loading && (
            <div className="stack box">
              <header className="stack page-title-bar">
                <h2>{data.criticalFunction.function}: Criticality</h2>
              </header>
              <section>
                <h3>Current Level Of Criticality</h3>
                <p>
                  <span
                    className={`pill selected level-${data.criticalFunction.levelOfCriticality}`}
                  >
                    {data.criticalFunction.levelOfCriticality
                      ? data.setting[
                          "term" + data.criticalFunction.levelOfCriticality
                        ]
                      : "Not Documented"}
                  </span>
                </p>
              </section>
              <section className="stack">
                {/* Fieldset is set as `disabled` if page mode is uneditable, all children form elements will be disabled */}
                <fieldset className="stack" disabled={!isPageInEditMode}>
                  <h3>Institutional Impacts</h3>
                  <React.Fragment>
                    <fieldset className="stack">
                      <PillGroupWithComment
                        legendText="Does your function support the ability to protect life and/or property?"
                        pillGroupName="criticalityQuestionAnswers.protectLifeProperty"
                        pillSelection={
                          data.criticalFunction.criticalityQuestionAnswers[
                            "protectLifeProperty"
                          ]
                        }
                        pillGroupComment={
                          data.criticalFunction.criticalityQuestionAnswers[
                            "protectLifePropertyComment"
                          ]
                        }
                        onPillGroupChange={handlePillChange}
                        onTextAreaFocus={handleTextareaFocus}
                      />
                      <PillGroupWithComment
                        legendText="Will the loss of this function relate to a loss of revenue?"
                        pillGroupName="criticalityQuestionAnswers.lossOfRevenue"
                        pillSelection={
                          data.criticalFunction.criticalityQuestionAnswers[
                            "lossOfRevenue"
                          ]
                        }
                        pillGroupComment={
                          data.criticalFunction.criticalityQuestionAnswers[
                            "lossOfRevenueComment"
                          ]
                        }
                        onPillGroupChange={handlePillChange}
                        onTextAreaFocus={handleTextareaFocus}
                      />
                      <PillGroupWithComment
                        legendText="Will the lack of recovery to this function cause legal harm to the institution?"
                        pillGroupName="criticalityQuestionAnswers.causeLegalHarm"
                        pillSelection={
                          data.criticalFunction.criticalityQuestionAnswers[
                            "causeLegalHarm"
                          ]
                        }
                        pillGroupComment={
                          data.criticalFunction.criticalityQuestionAnswers[
                            "causeLegalHarmComment"
                          ]
                        }
                        onPillGroupChange={handlePillChange}
                        onTextAreaFocus={handleTextareaFocus}
                      />
                      <PillGroupWithComment
                        legendText="Does this function directly impact Students?"
                        pillGroupName="criticalityQuestionAnswers.impactStudents"
                        pillSelection={
                          data.criticalFunction.criticalityQuestionAnswers[
                            "impactStudents"
                          ]
                        }
                        pillGroupComment={
                          data.criticalFunction.criticalityQuestionAnswers[
                            "impactStudentsComment"
                          ]
                        }
                        onPillGroupChange={handlePillChange}
                        onTextAreaFocus={handleTextareaFocus}
                      />
                      <PillGroupWithComment
                        legendText="Would this function directly cause penalties or fines for the institution?"
                        pillGroupName="criticalityQuestionAnswers.causePenaltiesOrFines"
                        pillSelection={
                          data.criticalFunction.criticalityQuestionAnswers[
                            "causePenaltiesOrFines"
                          ]
                        }
                        pillGroupComment={
                          data.criticalFunction.criticalityQuestionAnswers[
                            "causePenaltiesOrFinesComment"
                          ]
                        }
                        onPillGroupChange={handlePillChange}
                        onTextAreaFocus={handleTextareaFocus}
                      />
                      <PillGroupWithComment
                        legendText="Would this function directly cause harm to the reputation of the institution?"
                        pillGroupName="criticalityQuestionAnswers.harmReputation"
                        pillSelection={
                          data.criticalFunction.criticalityQuestionAnswers[
                            "harmReputation"
                          ]
                        }
                        pillGroupComment={
                          data.criticalFunction.criticalityQuestionAnswers[
                            "harmReputationComment"
                          ]
                        }
                        onPillGroupChange={handlePillChange}
                        onTextAreaFocus={handleTextareaFocus}
                      />
                    </fieldset>
                  </React.Fragment>

                  <section>
                    <h3>Recovery Time Objective (RTO)</h3>
                    <RTOSelect
                      recoveryTimes={data.recoveryTimeObjectives}
                      selectedRecoveryTimeId={
                        data.criticalFunction.recoveryTimeObjective
                          ? data.criticalFunction.recoveryTimeObjective.id
                          : "1"
                      }
                      onSelectChange={handleFormAnswerChange}
                    />
                  </section>

                  {isCriticalFunctionUpdating && (
                    <div className="stack box">
                      <div className="center">
                        <Spinner size={64} />
                      </div>
                    </div>
                  )}
                  {data.criticalFunction.suggestedLevelOfCriticality &&
                    !isCriticalFunctionUpdating && (
                      <React.Fragment>
                        <section>
                          <h3>Suggested Criticality</h3>
                          <p>
                            Based on the information you have provided above we will
                            suggest a level of criticality.
                          </p>
                        </section>

                        <section>
                          <h3>Suggested Level</h3>
                          <p>
                            <span
                              className={`pill selected level-${data.criticalFunction.suggestedLevelOfCriticality}`}
                            >
                              {data.criticalFunction.suggestedLevelOfCriticality
                                ? data.setting[
                                    "term" +
                                      data.criticalFunction
                                        .suggestedLevelOfCriticality
                                  ]
                                : "Not Documented"}
                            </span>
                          </p>
                        </section>

                        <div className="grid-wrapper">
                          <label
                            className="span-two-col"
                            htmlFor="agreeWithSuggestedCriticalitySelect"
                          >
                            Do you agree with this suggested level of criticality?
                          </label>

                          <React.Fragment>
                            {/* Note: We use the React Hook Form <Controller> HOC to allow uncontrolled inputs
                      from other libs to work properly */}
                            <Controller
                              name="agreeWithSuggestedCriticality"
                              control={control}
                              rules={{ required: isSubmitted }}
                              render={({ onChange, onBlur, value, name }) => (
                                <SelectionControlGroup
                                  type="radio"
                                  id="agreeWithSuggestedCriticalitySelect"
                                  name={name}
                                  inline
                                  value={value}
                                  defaultValue={value}
                                  disabled={!isPageInEditMode}
                                  onChange={(value) => {
                                    onChange(value)
                                  }}
                                  controls={[
                                    {
                                      key: "yes",
                                      label: "Yes",
                                      value: "yes"
                                    },
                                    {
                                      key: "no",
                                      label: "No",
                                      value: "no"
                                    }
                                  ]}
                                />
                              )}
                              defaultValue={
                                data.criticalFunction.levelOfCriticality === null
                                  ? null
                                  : data.criticalFunction
                                      .suggestedLevelOfCriticality ===
                                    data.criticalFunction.levelOfCriticality
                                  ? "yes"
                                  : "no"
                              }
                            />
                            {errors && errors.agreeWithSuggestedCriticality && (
                              <span className="validation-error">
                                Agreement choice is required{" "}
                                <Icon name={"alert-warning"} />
                              </span>
                            )}
                            {watch(
                              "agreeWithSuggestedCriticality",
                              data.criticalFunction.levelOfCriticality === null
                                ? null
                                : data.criticalFunction
                                    .suggestedLevelOfCriticality ===
                                  data.criticalFunction.levelOfCriticality
                                ? "yes"
                                : "no"
                            ) === "no" && (
                              <div className="stack span-two-col">
                                <label htmlFor="LOCSelect">
                                  Please choose the level of criticality better
                                  represents this function
                                </label>
                                <select
                                  id="LOCSelect"
                                  name="userSelectedLevelOfCriticality"
                                  ref={register({ required: true })}
                                  defaultValue={
                                    data.criticalFunction.levelOfCriticality
                                  }
                                >
                                  <option value="1">{data.setting.item1}</option>
                                  <option value="2">{data.setting.item2}</option>
                                  <option value="3">{data.setting.item3}</option>
                                  <option value="4">{data.setting.item4}</option>
                                </select>
                                <label htmlFor="criticalityAgreement">
                                  Please provide an explanation
                                </label>
                                <textarea
                                  id="criticalityAgreement"
                                  name="levelOfCriticalityExplanation"
                                  ref={register}
                                  defaultValue={
                                    data.criticalFunction
                                      .levelOfCriticalityExplanation
                                  }
                                ></textarea>
                                {errors && errors["levelOfCriticalityExplanation"] && (
                                  <span className="validation-error">
                                    Explanation is required{" "}
                                    <Icon name={"alert-warning"} />
                                  </span>
                                )}
                              </div>
                            )}
                          </React.Fragment>
                        </div>
                      </React.Fragment>
                    )}
                </fieldset>
              </section>
            </div>
          )}
        </form>
      </FormProvider>
      {isAlertOpen && (
        <Alert
          id={"alert"}
          color={"red"}
          icon={<Icon name={"alert-info"} />}
          message={
            "Not all required fields have been completed. Please complete and save again."
          }
          primaryButtonLabel={"Ok"}
          closeButtonLabel="Close"
          isOpen={isAlertOpen}
          autoFocus={false}
          onPrimaryButtonClicked={() => setIsAlertOpen(false)}
          onCloseButtonClicked={(e) => setIsAlertOpen(false)}
        />
      )}
    </React.Fragment>
  )
}
export default CriticalityLevels
